/** @jsx jsx */
import { Flex, Grid, Heading, Paragraph, PopoverDisclosure, usePopover } from "@eworkit/iris";
import { FC, useContext } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { LazyUserAvatar } from "@/components/avatars";
import { CollapsibleArea, CollapsibleButton, useCollapsible } from "@/components/collapsible";
import { UserPopover } from "@/components/popovers/UserPopover";
import { ResponsiveCard } from "@/components/ResponsiveCard";
import { UserContext } from "@/components/User";
import { JobApplication } from "@/data/job-applications/types/JobApplication";
import { formatFullName } from "@/data/persons";

interface JobApplicationDetailsContactsProps {
  jobApplication: JobApplication;
}

export const JobApplicationDetailsContacts: FC<JobApplicationDetailsContactsProps> = ({ jobApplication }) => {
  const { t } = useTranslation("session");

  const clientPopoverState = usePopover();
  const applicationContactPopoverState = usePopover();
  const candidatePopoverState = usePopover();

  const collapsibleState = useCollapsible(false);

  const User = useContext(UserContext);

  return (
    <ResponsiveCard sx={{ py: ["sm", "sm", "sm", "md"] }}>
      <Grid gap="sm">
        <Flex justifyContent="space-between" alignItems="center">
          <Heading size="h-sm">{t("JOB_APPLICATIONS.DETAIL.CONTACTS.HEADING")}</Heading>
          <CollapsibleButton {...collapsibleState} />
        </Flex>

        <CollapsibleArea {...collapsibleState}>
          <Flex gap="sm" flexDirection="column">
            {jobApplication?.jobRequest?.hiringManager && (
              <Flex flexDirection="column" gap="xxs">
                <Paragraph color="n700">{t("JOB_APPLICATIONS.DETAIL.CONTACTS.CLIENT_CONTACT")}</Paragraph>
                <PopoverDisclosure as={Paragraph} bold {...clientPopoverState}>
                  <Flex data-test="contacts-clientContact" gap="xs" alignItems="center">
                    <LazyUserAvatar size="xs" user={jobApplication.jobRequest.hiringManager} />
                    {formatFullName(jobApplication.jobRequest.hiringManager)}
                  </Flex>
                </PopoverDisclosure>
                {jobApplication.jobRequest.hiringManager && (
                  <UserPopover user={jobApplication.jobRequest.hiringManager} state={clientPopoverState} />
                )}
              </Flex>
            )}

            {User.contextCompany && (
              <Flex flexDirection="column" gap="xxs">
                <Paragraph color="n700">{t("JOB_APPLICATIONS.DETAIL.CONTACTS.APPLICATION_CONTACT")}</Paragraph>
                <PopoverDisclosure as={Paragraph} bold {...applicationContactPopoverState}>
                  <Flex data-test="contacts-applicationContact" gap="xs" alignItems="center">
                    <LazyUserAvatar size="xs" user={jobApplication.consultantProfile.contactPerson} />
                    {formatFullName(jobApplication.consultantProfile.contactPerson)}
                  </Flex>
                </PopoverDisclosure>
                <UserPopover
                  user={jobApplication.consultantProfile.contactPerson}
                  state={applicationContactPopoverState}
                  position={jobApplication.consultantProfile.contactPerson.position?.name}
                />
              </Flex>
            )}

            <Flex flexDirection="column" gap="xxs">
              <Paragraph color="n700">{t("JOB_APPLICATIONS.DETAIL.CONTACTS.CANDIDATE")}</Paragraph>
              <PopoverDisclosure as={Paragraph} bold {...candidatePopoverState}>
                <Flex data-test="contacts-candidate" gap="xs" alignItems="center">
                  <LazyUserAvatar size="xs" user={jobApplication.consultantProfile} />
                  {formatFullName(jobApplication.consultantProfile)}
                </Flex>
              </PopoverDisclosure>
              <UserPopover
                user={jobApplication.consultantProfile}
                position={jobApplication.consultantProfile.position?.name}
                state={candidatePopoverState}
              />
            </Flex>
          </Flex>
        </CollapsibleArea>
      </Grid>
    </ResponsiveCard>
  );
};
